import React, { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";

/* ============================================================================
   ASHA AD LAB — internal explorer for 10 Meta ad concepts.
   Every ad board is rendered live in CSS at 340x604 (9:16) — no image assets.
   ========================================================================== */

const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..700;1,9..144,300..700&family=IBM+Plex+Mono:ital,wght@0,400;0,500;0,600;1,400&family=Caveat:wght@600;700&display=swap');
`;

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

const serif = { fontFamily: "'Fraunces', Georgia, serif" };
const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const hand = { fontFamily: "'Caveat', cursive" };
const ios = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif",
};

/* ----------------------------------------------------------------------------
   AD BOARD COMPONENTS — each is a 340x604 artboard
---------------------------------------------------------------------------- */

const Board = ({ children, style }) => (
  <div
    className="relative overflow-hidden"
    style={{
      width: 340,
      height: 604,
      borderRadius: 24,
      flexShrink: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

/* 01 — BUILT AFTER (category / timeline) */
const AdTimeline = () => (
  <Board
    style={{
      background:
        "radial-gradient(120% 80% at 50% 0%, #10295c 0%, #0a1530 55%, #060d20 100%)",
    }}
  >
    <div
      className="absolute inset-0 opacity-[0.14]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(120,160,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(120,160,255,.35) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }}
    />
    <div className="relative h-full flex flex-col px-7 pt-8 pb-7">
      <div style={mono} className="text-[10px] tracking-[0.25em] text-blue-300/80">
        ASHA DENTAL
      </div>
      <h3
        style={serif}
        className="mt-4 text-[27px] leading-[1.12] text-white font-medium"
      >
        Every PMS you've ever used was built{" "}
        <em className="text-blue-300">before AI existed.</em>
      </h3>

      <div className="mt-7 flex-1 relative">
        <div className="absolute left-[7px] top-1 bottom-1 w-px bg-blue-300/25" />
        {[
          ["1985", "Dentrix", "client-server era"],
          ["1992", "Eaglesoft", "Windows 3.1 era"],
          ["2003", "Open Dental", "pre-smartphone era"],
        ].map(([yr, name, era]) => (
          <div key={name} className="relative pl-8 pb-6">
            <div className="absolute left-[3px] top-[5px] w-[9px] h-[9px] rounded-full border border-blue-300/50 bg-[#0a1530]" />
            <div style={mono} className="text-[10px] text-blue-300/60">
              {yr}
            </div>
            <div className="text-[15px] text-slate-300 font-semibold" style={ios}>
              {name}
              <span className="ml-2 text-[11px] font-normal text-slate-500">
                {era}
              </span>
            </div>
          </div>
        ))}
        <div className="relative pl-8">
          <div
            className="absolute left-[-1px] top-[2px] w-[17px] h-[17px] rounded-full bg-blue-400"
            style={{ boxShadow: "0 0 24px 6px rgba(96,165,250,.65)" }}
          />
          <div style={mono} className="text-[10px] text-blue-200">
            2025
          </div>
          <div className="text-[19px] text-white font-bold" style={ios}>
            Asha
          </div>
          <div className="text-[12.5px] text-blue-200/90 leading-snug" style={ios}>
            The first AI-native PMS. 20 dental AI products in one platform.
          </div>
        </div>
      </div>

      <div
        className="mt-5 rounded-full bg-white text-[#0a1530] text-center py-3 text-[13px] font-bold"
        style={ios}
      >
        See what AI-native actually means →
      </div>
    </div>
  </Board>
);

/* 02 — THE RECEIPT (all-in-one) */
const AdReceipt = () => (
  <Board style={{ background: "linear-gradient(170deg, #16306e, #0d1b3d 70%)" }}>
    <div className="relative h-full flex flex-col items-center px-6 pt-8 pb-6">
      <h3
        style={serif}
        className="text-[24px] leading-[1.15] text-[#e8f1ff] text-center font-medium"
      >
        The real cost of{" "}
        <em className="text-[#7dd3fc]">"best of breed."</em>
      </h3>

      <div
        className="mt-5 w-[252px] bg-[#f8fafc] px-5 pt-5 pb-7 shadow-2xl"
        style={{
          ...mono,
          clipPath:
            "polygon(0 0, 100% 0, 100% calc(100% - 8px), 95% 100%, 90% calc(100% - 8px), 85% 100%, 80% calc(100% - 8px), 75% 100%, 70% calc(100% - 8px), 65% 100%, 60% calc(100% - 8px), 55% 100%, 50% calc(100% - 8px), 45% 100%, 40% calc(100% - 8px), 35% 100%, 30% calc(100% - 8px), 25% 100%, 20% calc(100% - 8px), 15% 100%, 10% calc(100% - 8px), 5% 100%, 0 calc(100% - 8px))",
        }}
      >
        <div className="text-center text-[10px] tracking-[0.2em] text-stone-500">
          YOUR SOFTWARE STACK
        </div>
        <div className="text-center text-[9px] text-stone-400 mb-3">
          ITEMIZED · MONTHLY
        </div>
        {[
          ["Practice mgmt software", "$589"],
          ["Insurance verification", "$349"],
          ["AI receptionist", "$499"],
          ["Claim scrubbing", "$299"],
          ["Patient texting", "$329"],
          ["Analytics dashboard", "$449"],
          ["Phones / VoIP", "$250"],
          ["eFax", "$89"],
          ["Billing service", "6% coll."],
        ].map(([item, price]) => (
          <div
            key={item}
            className="flex justify-between text-[10.5px] text-stone-600 py-[3px] border-b border-dashed border-stone-200"
          >
            <span>{item}</span>
            <span>{price}</span>
          </div>
        ))}
        <div className="flex justify-between text-[12px] font-semibold text-stone-900 pt-2">
          <span>9 VENDORS</span>
          <span>$2,853+/mo</span>
        </div>
        <div
          className="absolute left-1/2 top-[295px] -translate-x-1/2 rotate-[-12deg] border-[3px] border-[#1d4ed8] text-[#1d4ed8] px-3 py-1 text-[15px] font-bold tracking-[0.12em] rounded"
          style={{ ...mono, background: "rgba(248,250,252,.78)" }}
        >
          REPLACED BY ASHA
        </div>
      </div>

      <div className="mt-auto text-center">
        <div className="text-[15px] text-[#e8f1ff] font-semibold" style={ios}>
          20 AI products. 1 platform. 1 invoice.
        </div>
        <div
          className="mt-3 inline-block rounded-full bg-white text-[#16306e] px-6 py-2.5 text-[13px] font-bold"
          style={ios}
        >
          Get a quote
        </div>
      </div>
    </div>
  </Board>
);

/* 03 — THE OPEN LETTER (category / manifesto) */
const AdOpenLetter = () => (
  <Board
    style={{
      background:
        "linear-gradient(165deg, #1c1e3a 0%, #141532 45%, #0d0e24 100%)",
    }}
  >
    <div className="relative h-full flex flex-col px-8 pt-9 pb-7">
      <div
        style={mono}
        className="text-[10.5px] tracking-[0.18em] leading-relaxed text-indigo-200/90 font-semibold"
      >
        AN OPEN LETTER TO EVERY
        <br />
        DENTAL FRONT DESK
      </div>
      <div style={serif} className="mt-5 text-[30px] italic text-white">
        We're sorry.
      </div>
      <div
        className="mt-5 space-y-[13px] text-[13.5px] leading-snug text-indigo-100/85"
        style={ios}
      >
        <p>You were hired to take care of patients.</p>
        <p>
          Instead, you spend your days on hold with insurance companies.
          Re-keying EOBs. Chasing claims. Calling a recall list that never gets
          shorter.
        </p>
        <p>That was never supposed to be the job.</p>
        <p className="text-white font-medium">
          Asha is the first AI-native PMS. The admin work is its job now.
        </p>
        <p className="text-white font-medium">You get the patients back.</p>
      </div>
      <div className="mt-auto">
        <div
          className="flex items-center justify-between rounded-xl bg-indigo-500/90 text-white px-4 py-3 text-[13px] font-semibold"
          style={ios}
        >
          <span>Learn more</span>
          <span>→</span>
        </div>
        <div style={mono} className="mt-3 text-[9.5px] text-indigo-300/50">
          ASHA DENTAL · THE FIRST AI-NATIVE PMS
        </div>
      </div>
    </div>
  </Board>
);

/* 04 — THE STICKY PILE (feature: back office) */
const stickies = [
  ["CALL Delta back re: claim #4471", "#fff3a3", -7, 18, 60],
  ["verify Mon's 26 pts!!", "#ffd1e0", 6, 138, 96],
  ["post EOBs — 47 left", "#c9f0ff", -4, 40, 178],
  ["recall list: 312 overdue 😬", "#d6ffc9", 8, 158, 208],
  ["appeal denial — Hernandez", "#fff3a3", -9, 22, 296],
  ["rebill #2210 w/ x-rays", "#ffd1e0", 5, 150, 322],
  ["Tues 10am cancelled — FILL IT", "#c9f0ff", -3, 78, 392],
];
const AdStickyPile = () => (
  <Board style={{ background: "linear-gradient(180deg, #1d4ed8, #16306e)" }}>
    <div
      className="absolute inset-0 opacity-25"
      style={{ backgroundImage: GRAIN, backgroundSize: "180px" }}
    />
    <div className="relative h-full px-7 pt-8">
      <h3 style={serif} className="text-[28px] leading-[1.1] text-white font-medium">
        Your front desk's brain, <em>before Asha.</em>
      </h3>
      <div className="absolute inset-x-0 top-[120px] bottom-[110px]">
        {stickies.map(([text, color, rot, x, y], i) => (
          <div
            key={i}
            className="absolute w-[150px] px-3 pt-3 pb-4 text-[16px] leading-[1.1] text-stone-800"
            style={{
              ...hand,
              background: color,
              left: x,
              top: y,
              transform: `rotate(${rot}deg)`,
              boxShadow: "0 10px 18px rgba(0,0,0,.28)",
            }}
          >
            {text}
          </div>
        ))}
      </div>
      <div className="absolute inset-x-6 bottom-6">
        <div
          className="rounded-2xl bg-white px-5 py-4 shadow-xl text-center"
          style={ios}
        >
          <div className="text-[14.5px] font-bold text-stone-900">
            Asha remembers all of it. And does it.
          </div>
          <div className="text-[11.5px] text-stone-500 mt-1">
            20 dental AI products in one platform → Book a demo
          </div>
        </div>
      </div>
    </div>
  </Board>
);

/* 05 — THE BIG QUESTION (feature: AR & collections) */
const AdBigQuestion = () => (
  <Board style={{ background: "#e9f1fb" }}>
    <div
      className="absolute inset-0 opacity-[0.18]"
      style={{ backgroundImage: GRAIN, backgroundSize: "160px" }}
    />
    <div className="relative h-full flex flex-col px-7 pt-9 pb-7">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-[#13294b]" />
        <span style={mono} className="text-[11px] tracking-[0.14em] text-[#13294b]">
          asha dental
        </span>
      </div>
      <h3
        style={serif}
        className="mt-10 text-[40px] leading-[1.06] text-[#13294b] font-medium"
      >
        How much did you write off last month because{" "}
        <em>nobody had time to chase it?</em>
      </h3>
      <div className="mt-auto">
        <div
          className="inline-flex items-center gap-2.5 rounded-full bg-[#13294b] pl-2 pr-4 py-2"
          style={ios}
        >
          <div className="flex -space-x-1.5">
            {["#60a5fa", "#93c5fd", "#dbeafe"].map((c) => (
              <div
                key={c}
                className="w-6 h-6 rounded-full border-2 border-[#13294b]"
                style={{ background: c }}
              />
            ))}
          </div>
          <div className="text-left">
            <div className="text-[12px] font-bold text-white leading-tight">
              AI AR &amp; Collections
            </div>
            <div className="text-[10px] text-[#9cc3f0] leading-tight">
              Built into the first AI-native PMS
            </div>
          </div>
        </div>
      </div>
    </div>
  </Board>
);

/* 06 — ON HOLD (feature: insurance verification) */
const AdOnHold = () => (
  <Board style={{ background: "linear-gradient(180deg, #dfe9f5, #c5d5ea)" }}>
    <div className="relative h-full flex flex-col px-6 pt-7 pb-6" style={ios}>
      <div className="rounded-[26px] bg-[#f6f7f9] shadow-xl px-5 pt-6 pb-5 text-center">
        <div className="text-[11px] text-stone-400 font-medium">calling…</div>
        <div className="text-[19px] font-semibold text-stone-900 mt-0.5">
          Delta Dental Provider Line
        </div>
        <div className="text-[26px] text-stone-500 font-light mt-1 tabular-nums">
          1:47:23
        </div>
        <div className="text-[11.5px] text-amber-600 font-medium mt-0.5">
          ♪ your call is important to us ♪
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 px-3">
          {["mute", "keypad", "speaker", "add", "FaceTime", "contacts"].map(
            (b) => (
              <div key={b} className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-full bg-stone-200" />
                <span className="text-[8.5px] text-stone-400">{b}</span>
              </div>
            )
          )}
        </div>
        <div className="mx-auto mt-4 w-11 h-11 rounded-full bg-red-500 flex items-center justify-center text-white text-[17px]">
          ⏚
        </div>
      </div>

      <h3
        style={serif}
        className="mt-5 text-[24px] leading-[1.12] text-[#13294b] font-medium text-center"
      >
        Hold music isn't a <em>job description.</em>
      </h3>

      <div className="mt-auto rounded-2xl bg-white shadow-lg px-4 py-3.5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-[12px] font-bold">
            A
          </div>
          <div className="text-[12px] font-semibold text-stone-800">
            Asha · this morning
          </div>
        </div>
        <div className="mt-2 space-y-1 text-[12px] text-stone-600">
          <div>✓ 38 insurance verifications completed</div>
          <div>✓ Full benefit breakdowns in every chart</div>
          <div className="font-semibold text-blue-700">
            ✓ 0 minutes of your team's time
          </div>
        </div>
      </div>
    </div>
  </Board>
);

/* 07 — 7:42 PM (feature: AI receptionist) */
const AdLockScreen = () => (
  <Board
    style={{
      background:
        "linear-gradient(180deg, #0f1f4d 0%, #1e3a8a 45%, #3565c0 80%, #6da3e8 100%)",
    }}
  >
    <div className="relative h-full flex flex-col px-5 pt-9 pb-6" style={ios}>
      <div className="text-center text-white/90">
        <div className="text-[15px] font-medium">Thursday, June 11</div>
        <div className="text-[64px] font-extralight leading-none mt-1 tracking-tight">
          7:42
        </div>
      </div>

      <div className="mt-7 space-y-2.5">
        <div className="rounded-2xl bg-white/25 backdrop-blur px-4 py-3">
          <div className="flex justify-between text-[10.5px] text-white/70">
            <span>📞 PHONE</span>
            <span>7:38 PM</span>
          </div>
          <div className="text-[13px] text-white font-semibold mt-0.5">
            Missed Call (2)
          </div>
          <div className="text-[12px] text-white/75">Unknown — Walnut Creek, CA</div>
        </div>

        <div
          className="rounded-2xl bg-white px-4 py-3.5 shadow-2xl"
          style={{ boxShadow: "0 18px 40px rgba(0,0,0,.35)" }}
        >
          <div className="flex justify-between text-[10.5px] text-stone-400">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex w-4 h-4 rounded bg-blue-600 text-white text-[9px] font-bold items-center justify-center">
                A
              </span>
              ASHA
            </span>
            <span>7:42 PM</span>
          </div>
          <div className="text-[13.5px] text-stone-900 font-semibold mt-1">
            New patient booked 🎉
          </div>
          <div className="text-[12px] text-stone-600 leading-snug mt-0.5">
            I answered Maya R.'s call, booked her for Tue 9:00 AM, verified her
            Delta PPO, and sent intake forms.
          </div>
        </div>
      </div>

      <div className="mt-auto text-center">
        <h3 style={serif} className="text-[23px] leading-[1.15] text-white font-medium">
          Your office closes at 5.
          <br />
          <em>New patients call at 7:42.</em>
        </h3>
        <div className="mt-3 text-[12px] text-white/80">
          AI Receptionist — built into the first AI-native PMS
        </div>
      </div>
    </div>
  </Board>
);

/* 08 — THE NIGHT SHIFT (platform proof) */
const AdNightShift = () => (
  <Board
    style={{
      background:
        "linear-gradient(180deg, #050a1c 0%, #0b1535 38%, #16306e 68%, #3b82f6 96%, #93c5fd 100%)",
    }}
  >
    <div className="absolute top-8 right-8 w-9 h-9 rounded-full bg-[#f5f0dc] opacity-90"
      style={{ boxShadow: "0 0 30px 8px rgba(245,240,220,.35)" }}
    />
    {[...Array(18)].map((_, i) => (
      <div
        key={i}
        className="absolute w-[2px] h-[2px] rounded-full bg-white/70"
        style={{ left: (i * 53) % 320 + 10, top: (i * 37) % 240 + 14 }}
      />
    ))}
    <div className="relative h-full flex flex-col px-7 pt-9 pb-7">
      <h3 style={serif} className="text-[27px] leading-[1.12] text-white font-medium">
        While your team slept, <em className="text-sky-300">Asha didn't.</em>
      </h3>
      <div className="mt-6 space-y-2.5" style={mono}>
        {[
          ["11:38 PM", "Scrubbed 14 claims for tomorrow's batch"],
          ["1:12 AM", "Posted 23 EOBs, reconciled to the penny"],
          ["2:47 AM", "Verified insurance for all 31 of today's patients"],
          ["5:05 AM", "Replied to 6 after-hours patient texts"],
          ["6:58 AM", "Filled this morning's cancellation"],
        ].map(([t, task]) => (
          <div
            key={t}
            className="flex gap-3 rounded-lg bg-white/[0.07] backdrop-blur-sm border border-white/10 px-3.5 py-2.5"
          >
            <span className="text-[10px] text-sky-300/90 w-[54px] shrink-0 pt-[2px]">
              {t}
            </span>
            <span className="text-[11.5px] text-white/90 leading-snug" style={ios}>
              {task}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-auto">
        <div className="text-[15px] text-white font-semibold text-center" style={ios}>
          The first PMS that works the night shift.
        </div>
        <div
          className="mt-3 rounded-full bg-white text-[#0b1535] text-center py-3 text-[13px] font-bold"
          style={ios}
        >
          Meet Asha →
        </div>
      </div>
    </div>
  </Board>
);

/* 09 — THE GROUP CHAT (all-in-one / social) */
const AdGroupChat = () => {
  const grey = "bg-[#e9e9eb] text-black self-start rounded-2xl rounded-bl-md";
  const blue = "bg-[#0a84ff] text-white self-end rounded-2xl rounded-br-md";
  return (
    <Board style={{ background: "linear-gradient(180deg, #bfd7f2, #9fc2e8)" }}>
      <div className="relative h-full flex flex-col px-4 pt-5 pb-5" style={ios}>
        <div className="rounded-[28px] bg-white shadow-2xl flex-1 overflow-hidden flex flex-col">
          <div className="border-b border-stone-100 py-3 text-center">
            <div className="mx-auto w-9 h-9 rounded-full bg-gradient-to-br from-stone-300 to-stone-400 flex items-center justify-center text-[15px]">
              🦷
            </div>
            <div className="text-[11.5px] font-semibold text-stone-800 mt-1">
              OM friends (4)
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-1.5 px-3.5 pt-3 text-[12.5px] leading-snug">
            <span className="text-[9px] text-stone-400 ml-2">Dana</span>
            <div className={`${grey} px-3 py-2 max-w-[78%]`}>
              how are you already done with EOBs?? it's 9:15
            </div>
            <div className={`${blue} px-3 py-2 max-w-[78%]`}>
              I'm not doing them. Asha is.
            </div>
            <span className="text-[9px] text-stone-400 ml-2 mt-1">Dana</span>
            <div className={`${grey} px-3 py-2 max-w-[78%]`}>
              your PMS does EOBs?
            </div>
            <div className={`${blue} px-3 py-2 max-w-[85%]`}>
              it's not just a PMS. verification, claims, recall, billing,
              phones… 20 AI products in one platform
            </div>
            <span className="text-[9px] text-stone-400 ml-2 mt-1">Priya</span>
            <div className={`${grey} px-3 py-2 max-w-[70%]`}>
              ok wait. I need a demo
            </div>
            <div className="self-end mt-1 w-[78%] rounded-xl overflow-hidden border border-stone-200">
              <div className="bg-gradient-to-br from-blue-600 to-sky-500 px-3 py-5 text-center text-white text-[13px] font-bold leading-tight">
                20 dental AI products
                <br />
                in 1 platform
              </div>
              <div className="bg-stone-50 px-3 py-1.5 text-[9.5px] text-stone-500">
                asha.health
              </div>
            </div>
          </div>
          <div className="px-3.5 py-2.5">
            <div className="rounded-full border border-stone-200 px-3 py-1.5 text-[11px] text-stone-400">
              iMessage
            </div>
          </div>
        </div>
      </div>
    </Board>
  );
};

/* 10 — THE WALL (social proof) */
const AdTestimonialWall = () => (
  <Board
    style={{
      background:
        "radial-gradient(140% 100% at 50% 0%, #3b82f6 0%, #2563eb 55%, #1d4ed8 100%)",
    }}
  >
    <div className="relative h-full flex flex-col px-6 pt-9 pb-7" style={ios}>
      <h3 style={serif} className="text-[28px] leading-[1.12] text-white font-medium">
        The AI-native PMS so good, <em>dentists are switching.</em>
      </h3>
      <div className="relative flex-1 mt-5">
        {[
          [
            "If my staff went on vacation for a week, I'd rest easy.",
            "Dentist",
            -3,
            0,
            0,
          ],
          [
            "Asha's support team respond so fast that I thought they were AI.",
            "Office manager",
            2,
            26,
            118,
          ],
          [
            "The amount of production Asha's AI has driven for us makes it a no-brainer.",
            "Dentist",
            -2,
            8,
            224,
          ],
        ].map(([q, who, rot, x, y], i) => (
          <div
            key={i}
            className="absolute rounded-2xl bg-white px-4 py-3.5 w-[270px]"
            style={{
              left: x,
              top: y,
              transform: `rotate(${rot}deg)`,
              boxShadow: "0 16px 36px rgba(13,38,99,.35)",
            }}
          >
            <div className="text-[13px] text-stone-800 leading-snug font-medium">
              "{q}"
            </div>
            <div style={mono} className="text-[9.5px] text-stone-400 mt-2">
              {who}
            </div>
            <div className="absolute -right-2 -top-2 bg-red-500 text-white text-[10px] rounded-full px-2 py-[3px] shadow">
              ♥
            </div>
          </div>
        ))}
      </div>
      <div className="mt-auto">
        <div
          className="rounded-full bg-white text-blue-700 text-center py-3 text-[13px] font-bold"
        >
          Book a demo — see why they switched
        </div>
        <div style={mono} className="mt-3 text-center text-[9px] text-blue-200/80">
          ASHA · THE FIRST AI-NATIVE DENTAL PMS
        </div>
      </div>
    </div>
  </Board>
);

/* 11 — POSITION FILLED (feature: AI billing, end to end) */
const AdJobPost = () => (
  <Board style={{ background: "linear-gradient(180deg, #eef0f4, #dde2ea)" }}>
    <div
      className="absolute inset-0 opacity-[0.14]"
      style={{ backgroundImage: GRAIN, backgroundSize: "170px" }}
    />
    <div className="relative h-full flex flex-col px-6 pt-7 pb-6" style={ios}>
      <div className="relative rounded-2xl bg-white shadow-xl px-5 pt-5 pb-4 border border-stone-200/60">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[15.5px] font-bold text-stone-900 leading-tight">
              Dental Billing Coordinator
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              Your Practice · Full-time · On-site
            </div>
          </div>
          <div className="text-[10px] text-stone-400 border border-stone-200 rounded px-1.5 py-0.5">
            ☆ Save
          </div>
        </div>
        <div className="mt-2 inline-block rounded bg-blue-50 text-blue-700 text-[10.5px] font-semibold px-2 py-0.5">
          $58,000–$66,000 / yr
        </div>
        <ul className="mt-3 space-y-[7px] text-[11.5px] text-stone-600 leading-snug list-disc pl-4">
          <li>Spend 2+ hours/day on hold with payers</li>
          <li>Post 40+ EOBs daily, zero typos allowed</li>
          <li>Appeal denials. Resubmit. Repeat.</li>
          <li>Chase the 90+ day AR bucket, forever</li>
          <li>Send patient statements nobody reads</li>
        </ul>
        <div className="mt-3.5 flex items-center justify-between">
          <div className="rounded-lg bg-blue-600 text-white text-[11.5px] font-semibold px-4 py-2">
            Apply now
          </div>
          <div className="text-[9.5px] text-stone-400">
            Posted 47 days ago · 3 applicants
          </div>
        </div>
        <div
          className="absolute left-1/2 top-[108px] -translate-x-1/2 rotate-[-10deg] border-[3.5px] border-[#1d4ed8] text-[#1d4ed8] px-4 py-1.5 rounded text-center"
          style={{ ...mono, background: "rgba(255,255,255,.82)" }}
        >
          <div className="text-[16px] font-bold tracking-[0.1em] leading-none">
            POSITION FILLED
          </div>
          <div className="text-[9px] tracking-[0.3em] mt-1">BY ASHA AI</div>
        </div>
      </div>

      <h3
        style={serif}
        className="mt-6 text-[27px] leading-[1.1] text-[#101828] font-medium"
      >
        AI that does <em>all</em> of your dental billing.
      </h3>
      <p className="mt-2 text-[12.5px] leading-snug text-stone-500">
        Claims, EOBs, denials, AR, patient statements — end to end. No
        recruiter fees. No training. No turnover.
      </p>

      <div className="mt-auto">
        <div className="rounded-full bg-[#101828] text-white text-center py-3 text-[13px] font-bold">
          See it work a full day →
        </div>
        <div style={mono} className="mt-3 text-center text-[9px] text-stone-400 tracking-[0.12em]">
          ASHA · THE FIRST AI-NATIVE DENTAL PMS
        </div>
      </div>
    </div>
  </Board>
);

/* 12 — THE DENIAL (feature: AI billing, denials & appeals) */
const AdDenialLetter = () => (
  <Board
    style={{
      background: "linear-gradient(170deg, #14254e 0%, #0c1733 70%, #081025 100%)",
    }}
  >
    <div
      className="absolute inset-0 opacity-20"
      style={{ backgroundImage: GRAIN, backgroundSize: "180px" }}
    />
    <div className="relative h-full flex flex-col px-6 pt-8 pb-6">
      <div
        className="relative mx-auto w-[268px] bg-[#f8fafc] px-5 pt-5 pb-6 shadow-2xl"
        style={{ ...mono, transform: "rotate(-1.5deg)" }}
      >
        <div className="text-[10px] tracking-[0.18em] text-stone-500 font-semibold">
          DELTA DENTAL OF CALIFORNIA
        </div>
        <div className="text-[9px] text-stone-400">EXPLANATION OF BENEFITS</div>
        <div className="mt-3 space-y-[5px] text-[10px] text-stone-600">
          <div className="flex justify-between">
            <span>Claim #88-4471</span>
            <span>D4341 ×2</span>
          </div>
          <div className="flex justify-between border-t border-dashed border-stone-200 pt-[5px]">
            <span>STATUS</span>
            <span className="text-[#c2371f] font-bold">DENIED</span>
          </div>
          <div className="text-stone-500 leading-snug">
            Reason 197: documentation insufficient to establish clinical
            necessity.
          </div>
          <div className="flex justify-between border-t border-dashed border-stone-200 pt-[5px]">
            <span>Paid to provider</span>
            <span className="font-bold">$0.00</span>
          </div>
        </div>
        <div
          className="absolute left-1/2 bottom-[-26px] w-[230px] -translate-x-1/2 rotate-[-8deg] border-[3px] border-[#1d4ed8] text-[#1d4ed8] px-3 py-2 rounded text-center"
          style={{ background: "rgba(248,250,252,.9)" }}
        >
          <div className="text-[13px] font-bold tracking-[0.1em] leading-none">
            APPEALED &amp; PAID — $1,847
          </div>
          <div className="text-[8.5px] tracking-[0.28em] mt-1">BY ASHA AI</div>
        </div>
      </div>

      <h3
        style={serif}
        className="mt-12 text-[27px] leading-[1.12] text-[#eaf2ff] font-medium"
      >
        Asha doesn't take <em className="text-[#7dd3fc]">no</em> for an answer.
      </h3>
      <p className="mt-2 text-[12.5px] leading-snug text-[#9db8e0]" style={ios}>
        AI appeals every denial — narrative written, x-rays and perio charting
        attached — then follows up until it's paid.
      </p>

      <div className="mt-auto">
        <div
          className="rounded-full bg-[#eaf2ff] text-[#14254e] text-center py-3 text-[13px] font-bold"
          style={ios}
        >
          See AI billing in action →
        </div>
        <div style={mono} className="mt-3 text-center text-[9px] text-[#7d96c8] tracking-[0.12em]">
          ASHA · THE FIRST AI-NATIVE DENTAL PMS
        </div>
      </div>
    </div>
  </Board>
);

/* 13 — TO THE PENNY (feature: AI billing, EOB posting & reconciliation) */
const AdLedger = () => (
  <Board
    style={{
      background: "linear-gradient(175deg, #10254f 0%, #0a1838 60%, #060f24 100%)",
    }}
  >
    <div className="relative h-full flex flex-col px-6 pt-8 pb-6">
      <div style={mono} className="text-[10px] tracking-[0.25em] text-sky-300/70">
        OVERNIGHT POSTING LOG
      </div>
      <h3
        style={serif}
        className="mt-3 text-[26px] leading-[1.12] text-white font-medium"
      >
        Posted overnight.{" "}
        <em className="text-sky-300">Reconciled to the penny.</em>
      </h3>

      <div
        className="mt-6 rounded-xl border border-sky-300/15 bg-black/30 px-4 py-4"
        style={mono}
      >
        {[
          ["EOB #2210", "Delta PPO", "$1,284.50"],
          ["EOB #2211", "Cigna", "$86.00"],
          ["EOB #2212", "MetLife", "$743.20"],
          ["EOB #2213", "Guardian", "$402.75"],
          ["EOB #2214", "Aetna", "$1,118.06"],
        ].map(([id, payer, amt]) => (
          <div
            key={id}
            className="flex items-center justify-between py-[7px] border-b border-sky-300/10 text-[10.5px]"
          >
            <span className="text-blue-100/60">{id}</span>
            <span className="text-blue-100/45">{payer}</span>
            <span className="text-blue-100/90 w-[72px] text-right">{amt}</span>
            <span className="text-sky-400 ml-2">✓</span>
          </div>
        ))}
        <div className="flex justify-between pt-3 text-[11px]">
          <span className="text-sky-200/80">23 EOBs POSTED</span>
          <span className="text-sky-300 font-bold">VARIANCE $0.00</span>
        </div>
      </div>

      <p className="mt-4 text-[12.5px] leading-snug text-blue-100/55" style={ios}>
        Line items, adjustments, patient portions — posted by AI while your
        team sleeps. Anything unusual gets flagged for a human, not buried.
      </p>

      <div className="mt-auto">
        <div
          className="rounded-full bg-sky-300 text-[#060f24] text-center py-3 text-[13px] font-bold"
          style={ios}
        >
          Book a demo →
        </div>
        <div style={mono} className="mt-3 text-center text-[9px] text-sky-200/40 tracking-[0.12em]">
          ASHA · THE FIRST AI-NATIVE DENTAL PMS
        </div>
      </div>
    </div>
  </Board>
);

/* 00 — THE OPERATING SYSTEM (all-in-one, full inventory) */
const OS_SOFTWARES = [
  "Practice Management System",
  "Communication Suite",
  "Marketing Suite",
  "eClaims Portal",
  "Revenue Intelligence",
  "SOTA Cloud Imaging",
  "Insurance Verification",
  "Integrated Payments",
  "Patient Portal",
  "ePrescribe (DoseSpot)",
  "Online Scheduling",
];
const OS_AI_PRODUCTS = [
  "AI Receptionist",
  "AI Schedule Optimizer",
  "AI Hygiene Recall",
  "AI Patient Experience",
  "AI Case Acceptance",
  "AI Patient Intake",
  "AI Practice Manager",
  "AI Fax Manager",
  "AI Insurance Verification",
  "AI Claim Submission",
  "AI Claim Scrubbing",
  "AI Denial Management",
  "AI Patient Billing",
  "AI AR & Collections",
  "AI EOB Posting",
  "AI Insurance Calls",
  "AI Copilot",
  "AI Scribe",
  "AI Treatment Planner",
  "AI Revenue Analytics",
];
const AdFullStack = () => (
  <Board
    style={{
      background:
        "radial-gradient(130% 90% at 50% 0%, #1e40af 0%, #16306e 45%, #0d1b3d 100%)",
    }}
  >
    <div
      className="absolute inset-0 opacity-[0.1]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(147,197,253,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(147,197,253,.4) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    />
    <div className="relative h-full flex flex-col px-6 pt-8 pb-6">
      <h3 style={serif} className="text-[30px] leading-[1.08] text-white font-semibold">
        The world's only <em className="text-sky-300">AI operating system</em>{" "}
        for dentistry.
      </h3>

      <div className="mt-5 flex-1 flex flex-col gap-3 min-h-0">
        {/* AI products — stacked first */}
        <div className="flex-[3] rounded-xl bg-sky-400/[0.12] border border-sky-300/25 px-3 pt-2.5 pb-2.5 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span style={mono} className="text-[8px] tracking-[0.2em] text-sky-200">
              AI PRODUCTS
            </span>
            <span style={mono} className="text-[8px] font-bold text-sky-300">
              ×{OS_AI_PRODUCTS.length}
            </span>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-[4px] content-evenly">
            {OS_AI_PRODUCTS.map((s) => (
              <div key={s} className="flex items-center gap-1.5">
                <span className="w-[5px] h-[5px] rounded-full bg-sky-400 shrink-0" />
                <span className="text-[9px] text-white/95 leading-[1.3]" style={ios}>
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Softwares — below */}
        <div className="flex-[2] rounded-xl bg-white/[0.06] border border-white/10 px-3 pt-2.5 pb-2.5 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span style={mono} className="text-[8px] tracking-[0.2em] text-blue-200/90">
              SOFTWARES
            </span>
            <span style={mono} className="text-[8px] font-bold text-sky-300">
              ×{OS_SOFTWARES.length}
            </span>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-[4px] content-evenly">
            {OS_SOFTWARES.map((s) => (
              <div key={s} className="flex items-center gap-1.5">
                <span className="text-sky-400 text-[7.5px]">✓</span>
                <span className="text-[9px] text-blue-50/90 leading-[1.3]" style={ios}>
                  {s}
                </span>
              </div>
            ))}
          </div>
          <div style={mono} className="mt-2 pt-2 border-t border-white/10 text-[7px] leading-snug text-blue-200/50 tracking-[0.06em]">
            REPLACES DENTRIX, WEAVE, ZUUB, DENTAL INTEL + 5 MORE
          </div>
        </div>
      </div>
    </div>
  </Board>
);

/* 14 — THE HERO FEATURE (series template: one feature big, everything else listed) */
const HERO_REST = [
  "AI Receptionist",
  "AI Schedule Optimizer",
  "AI Hygiene Recall",
  "AI Patient Experience",
  "AI Case Acceptance",
  "AI Patient Intake",
  "AI Practice Manager",
  "AI Fax Manager",
  "AI Insurance Verification",
  "AI Claim Submission",
  "AI Claim Scrubbing",
  "AI Denial Management",
  "AI Patient Billing",
  "AI AR & Collections",
  "AI Insurance Calls",
  "AI Copilot",
  "AI Scribe",
  "AI Treatment Planner",
  "AI Revenue Analytics",
  "AI-Voice Perio Charting",
  "Full PMS",
  "SOTA Cloud Imaging",
  "DoseSpot ePrescribe",
  "VoIP Phones",
  "2-Way Texting",
  "Digital Forms",
  "Text-to-Pay",
  "Online Scheduling",
];
const AdHeroFeature = () => (
  <Board
    style={{
      background:
        "radial-gradient(120% 80% at 50% 0%, #10295c 0%, #0a1530 55%, #060d20 100%)",
    }}
  >
    <div className="relative h-full flex flex-col px-6 pt-7 pb-6">
      <div style={mono} className="text-[9.5px] tracking-[0.25em] text-blue-300/80">
        ASHA · AI EOB POSTING
      </div>

      {/* Hero graphic: glowing posting card */}
      <div className="relative mt-4">
        <div
          className="absolute -inset-3 rounded-2xl"
          style={{ background: "radial-gradient(60% 80% at 50% 50%, rgba(56,130,246,.45), transparent 75%)" }}
        />
        <div className="relative rounded-xl bg-[#f8fafc] shadow-2xl px-4 pt-3.5 pb-3" style={mono}>
          <div className="flex justify-between text-[8px] tracking-[0.16em] text-slate-400">
            <span>EOB QUEUE</span>
            <span>1:12 AM</span>
          </div>
          {[
            ["EOB #2211", "Cigna", "$86.00"],
            ["EOB #2212", "MetLife", "$743.20"],
            ["EOB #2214", "Aetna", "$1,118.06"],
          ].map(([id, payer, amt]) => (
            <div
              key={id}
              className="flex items-center justify-between py-[5px] border-b border-slate-100 text-[9.5px] text-slate-600"
            >
              <span>{id}</span>
              <span className="text-slate-400">{payer}</span>
              <span className="w-[64px] text-right font-semibold">{amt}</span>
              <span className="text-sky-500 ml-1.5">✓</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 text-[9.5px]">
            <span className="text-slate-500">POSTED WHILE YOU SLEPT</span>
            <span className="text-blue-700 font-bold">VARIANCE $0.00</span>
          </div>
          <div
            className="absolute -right-2 -top-3 rotate-[8deg] rounded bg-blue-600 text-white px-2.5 py-1 text-[9px] font-bold tracking-[0.14em] shadow-lg"
          >
            AUTO-POSTED
          </div>
        </div>
      </div>

      <h3 style={serif} className="mt-4 text-[23px] leading-[1.12] text-white font-medium">
        AI that posts your EOBs. <em className="text-sky-300">Automatically.</em>
      </h3>

      <div className="mt-4 flex items-center gap-2">
        <div className="h-px flex-1 bg-blue-300/20" />
        <span style={mono} className="text-[7.5px] tracking-[0.22em] text-blue-200/80">
          PLUS ALL OF THIS, SAME PLATFORM
        </span>
        <div className="h-px flex-1 bg-blue-300/20" />
      </div>

      <div className="mt-2.5 flex flex-wrap gap-[4px] content-start">
        {HERO_REST.map((p) => (
          <span
            key={p}
            className="rounded-full border border-blue-300/25 bg-white/[0.07] px-[6px] py-[2px] text-[6.8px] text-blue-50/90 leading-none"
            style={ios}
          >
            {p}
          </span>
        ))}
      </div>

      <div className="mt-auto">
        <div
          className="rounded-full bg-white text-[#0a1530] text-center py-2.5 text-[12.5px] font-bold"
          style={ios}
        >
          Explore all of them →
        </div>
      </div>
    </div>
  </Board>
);

/* ----------------------------------------------------------------------------
   CONCEPT DATA
---------------------------------------------------------------------------- */

const ADS = [
  {
    id: "the-os",
    no: "00",
    title: "The Operating System",
    tag: "all-in-one",
    board: AdFullStack,
    hook: "The entire inventory — every software and every AI product — on one board. Breadth IS the creative.",
    inspiration: "The AI OS deck's '10 softwares + 20 AI products in 1 platform' emphasis slide, compressed into a single ad unit.",
    why: "No competitor can publish this list. Most ads claim 'all-in-one'; this one proves it by enumeration — the reader's eye scrolls 31 line items and the conclusion forms on its own. It also pre-answers the 'what does it replace?' objection in-unit.",
    copy: [
      {
        label: "Full inventory",
        primaryText:
          "Practice management. Imaging. Phones. Texting. Claims. Verification. Payments. Marketing. Plus 20 AI products that do the work: reception, scheduling, recall, intake, billing, EOBs, denials, collections, scribe, treatment planning. One platform, one login, one invoice. Asha is the world's only AI operating system for dentistry.",
        headline: "The AI operating system for dentistry",
        cta: "Learn More",
      },
      {
        label: "Count it",
        primaryText:
          "Count what you're paying for separately right now: PMS, imaging, phones, texting, verification, claims, analytics, an answering service. Asha is over 10 softwares and 20 AI products in one operating system — with the goal of running as much of your practice on autopilot as possible.",
        headline: "Over 10 softwares + 20 AI products. One OS.",
        cta: "Learn More",
      },
      {
        label: "Autopilot",
        primaryText:
          "Asha combines over 10 softwares and 20 AI products into one connected system with a single goal: run as much of your practice on autopilot as possible. The front desk answers patients. The AI does everything else.",
        headline: "Your practice, on autopilot",
        cta: "Book Demo",
      },
    ],
    production:
      "Dense by design — run 9:16 full-bleed where the type is largest. Pair with a 6s video where the two columns type themselves in. Keep list text ≥18px equivalent at 1080 wide for legibility.",
  },
  {
    id: "built-after",
    no: "01",
    title: "Built After",
    tag: "category",
    board: AdTimeline,
    hook: "Reframe every competitor as a relic of a pre-AI world — without naming a single feature.",
    inspiration: "Category-creation plays; Archy's 'one software' positioning, inverted into a history lesson.",
    why: "Dentists have seen 100 feature ads. Nobody else can claim the timeline — 'first AI-native' is a structural moat, and the dotted gap between 2003 and 2025 makes the viewer feel how stale their current PMS is.",
    copy: [
      {
        label: "Timeline",
        primaryText:
          "Dentrix shipped in 1985. Eaglesoft in 1992. Open Dental in 2003. Every PMS you've ever used was designed before AI existed — and it shows. Asha is the first AI-native PMS: 20 dental AI products in one platform, built from scratch so the software does the admin work, not your team.",
        headline: "The first AI-native dental PMS",
        cta: "Learn More",
      },
      {
        label: "Punchy",
        primaryText:
          "Your PMS was built before the iPhone. Before Google. Before AI. Asha is the first AI-native PMS — 20 dental AI products in one platform, so the software does the admin work, not your team.",
        headline: "Built after AI. It shows.",
        cta: "Learn More",
      },
      {
        label: "Question",
        primaryText:
          "Quick question: what year was your PMS designed? If the answer is before 2023, it was built for a world without AI. Asha is the first AI-native PMS — built from scratch so the software runs the admin, not your team.",
        headline: "What year was your PMS built?",
        cta: "Learn More",
      },
    ],
    production: "Pure typography + timeline; trivial to produce as a static. Strong candidate for a 6s animated version where the timeline draws itself.",
  },
  {
    id: "receipt",
    no: "02",
    title: "The Receipt",
    tag: "all-in-one",
    board: AdReceipt,
    hook: "Itemize the 9-vendor software stack on a literal receipt, then stamp it REPLACED.",
    inspiration: "Lassie's physical-metaphor ads; pricing-anchor ads from fintech.",
    why: "Owners feel vendor fatigue monthly but never see it totaled. The receipt makes the invisible overhead visible and gives the all-in-one pitch a number to beat. The stamp delivers the punchline without copy.",
    copy: [
      {
        label: "Itemized",
        primaryText:
          "Verification tool. AI receptionist. Claim scrubber. Texting platform. Analytics. eFax. VoIP. A billing service taking 6%. And under it all, a PMS from the 90s holding it together. Asha replaces the whole stack: 20 dental AI products in one platform, one login, one invoice.",
        headline: "Your software stack is its own overhead",
        cta: "Get Quote",
      },
      {
        label: "Short math",
        primaryText:
          "9 vendors. 9 logins. 9 invoices. $2,800+ a month — stacked on top of a PMS from the 90s. Asha replaces the whole pile: 20 dental AI products, one platform, one invoice.",
        headline: "One platform. One invoice.",
        cta: "Get Quote",
      },
      {
        label: "Vendor fatigue",
        primaryText:
          "Every point solution promised to save time. Now you manage nine of them. Asha is the first AI-native PMS — verification, billing, phones, texting, analytics, all built in. Cancel the stack, keep the outcomes.",
        headline: "Cancel the stack",
        cta: "Get Quote",
      },
    ],
    production: "Shoot as flat-lay photo of a printed receipt for extra realism, or ship the CSS version as-is. Update prices to match real competitor list pricing before launch.",
  },
  {
    id: "open-letter",
    no: "03",
    title: "The Open Letter",
    tag: "category",
    board: AdOpenLetter,
    hook: "An apology to front desks for what the job became — empathy first, product second.",
    inspiration: "Toothy.ai's 'open letter to dental teams buried in insurance work.'",
    why: "Office managers heavily influence PMS decisions and are the ones drowning. Long-copy letter format stops the scroll precisely because it doesn't look like an ad — Toothy proved the format works in this exact vertical.",
    copy: [
      {
        label: "The letter",
        primaryText:
          "You were hired to take care of patients. Instead: hold music, EOBs, claim follow-ups, recall lists. That was never supposed to be the job. Asha is the first AI-native PMS — it verifies, posts, scrubs, calls, and chases so your team doesn't have to. Your team gets the patients back.",
        headline: "We're sorry. The admin was never supposed to be your job.",
        cta: "Learn More",
      },
      {
        label: "Short cut",
        primaryText:
          "To every dental front desk: you were hired for patients, not hold music. Asha — the first AI-native PMS — verifies, posts, scrubs, calls, and chases so you don't have to. You get the patients back.",
        headline: "The admin was never the job",
        cta: "Learn More",
      },
      {
        label: "Owner POV",
        primaryText:
          "Your best front desk person doesn't quit dentistry. They quit the hold music, the EOBs, and the recall list that never shrinks. Asha is the first AI-native PMS — it takes the admin so your team keeps the patients.",
        headline: "Keep your best people on patients",
        cta: "Learn More",
      },
    ],
    production: "Text-only — fastest concept to ship. Run as both 4:5 feed static and a slow-scroll story video with line-by-line reveals.",
  },
  {
    id: "sticky-pile",
    no: "04",
    title: "The Sticky Pile",
    tag: "feature",
    feature: "AI Back Office (EOBs, claims, recall)",
    board: AdStickyPile,
    hook: "The front desk's mental load, rendered as a chaotic pile of post-its — then one calm card from Asha.",
    inspiration: "Lassie's envelope-pile 'Insurance billing Before Lassie' ad.",
    why: "Visceral recognition: every OM has this exact wall of notes. Chaos→calm is the oldest before/after trick because it works, and the handwriting makes it feel human, not corporate.",
    copy: [
      {
        label: "Before/after",
        primaryText:
          "Call Delta back. Verify Monday's patients. Post 47 EOBs. Chase the 90+ bucket. Fill Tuesday's cancellation. Your front desk shouldn't need a photographic memory and three pads of post-its. Asha — the first AI-native PMS — remembers all of it, and does it.",
        headline: "Retire the post-it system",
        cta: "Book Demo",
      },
      {
        label: "Liability",
        primaryText:
          "If your practice runs on post-its and one person's photographic memory, that's not a workflow — it's a single point of failure. Asha — the first AI-native PMS — remembers all of it, and actually does it.",
        headline: "Retire the post-it system",
        cta: "Book Demo",
      },
      {
        label: "OM empathy",
        primaryText:
          "Call Delta back. Verify Monday's 26. Post 47 EOBs. Fill Tuesday's cancellation. Your office manager carries a to-do list no human should carry. Asha carries it instead — and clears it overnight.",
        headline: "Give your OM their brain back",
        cta: "Book Demo",
      },
    ],
    production: "Could also be shot practically (real post-its on a real monitor) for a UGC-feel variant. A/B the red background vs. a neutral office photo.",
  },
  {
    id: "big-question",
    no: "05",
    title: "The Big Question",
    tag: "feature",
    feature: "AI AR & Collections",
    board: AdBigQuestion,
    hook: "One uncomfortable question in huge type. No product shot, no UI, no list.",
    inspiration: "Codent's 'How much dental treatment are you not scheduling?'",
    why: "Owners know their write-off number is bad but avoid looking at it. A pointed question creates an open loop the demo promises to close. Maximum contrast with the cluttered blue UI ads in the same feed.",
    copy: [
      {
        label: "Write-offs",
        primaryText:
          "Most practices write off thousands every month — not because claims were wrong, but because nobody had time to chase them. Asha's AI AR & Collections works every claim, every appeal, every aging bucket, automatically. It's one of 20 AI products inside the first AI-native PMS.",
        headline: "Stop donating production to insurance companies",
        cta: "Learn More",
      },
      {
        label: "Time problem",
        primaryText:
          "Write-offs aren't a billing problem — they're a time problem. Nobody has hours to chase every claim, appeal, and aging bucket. Asha's AI does, automatically, inside the first AI-native PMS.",
        headline: "Stop writing off what you earned",
        cta: "Learn More",
      },
      {
        label: "Stat-led",
        primaryText:
          "The average practice leaves five figures a year in unworked AR. Not wrong claims — unchased ones. Asha's AI AR & Collections works every claim, every appeal, every bucket while your team works patients.",
        headline: "Unchased isn't uncollectable",
        cta: "Book Demo",
      },
    ],
    production: "Template-able: swap the question to target any feature (no-shows → Schedule Optimizer, unscheduled treatment → Treatment Planner). This is a question-bank format, not one ad.",
  },
  {
    id: "on-hold",
    no: "06",
    title: "On Hold",
    tag: "feature",
    feature: "AI Insurance Verification & Calling",
    board: AdOnHold,
    hook: "A call screen frozen at 1:47:23 of hold time vs. Asha's morning: 38 verifications, zero hold minutes.",
    inspiration: "DayDream's stat-led ROI ads; iOS-UI native formats.",
    why: "Hold time is the single most hated task in the front office. The timer is instantly legible in-feed, and the split contrast (their morning vs. Asha's morning) carries the value prop without a word of explanation.",
    copy: [
      {
        label: "Hold time",
        primaryText:
          "The average dental office spends 2+ hours a day on hold with payers. Asha's AI calls them instead — verifications, benefit breakdowns, claim statuses — and writes everything back into the chart. 38 verifications before your coffee. Zero hold music.",
        headline: "Hold music isn't a job description",
        cta: "Book Demo",
      },
      {
        label: "Annualized",
        primaryText:
          "Two hours a day on hold with payers is 500 hours a year of salaried time spent listening to smooth jazz. Asha's AI makes the calls instead — verifications, breakdowns, claim statuses — and writes it all back into the chart.",
        headline: "End the hold-music era",
        cta: "Book Demo",
      },
      {
        label: "Receipts",
        primaryText:
          "This morning, Asha verified 38 patients, pulled full benefit breakdowns, and checked 12 claim statuses. Total staff time: zero minutes. That's what AI-native means.",
        headline: "38 verifications. 0 hold minutes.",
        cta: "Book Demo",
      },
    ],
    production: "Also works as a video: 3 seconds of hold music with the timer ticking, hard cut to the Asha task feed. Sound-on version could be a top performer.",
  },
  {
    id: "742pm",
    no: "07",
    title: "7:42 PM",
    tag: "feature",
    feature: "AI Receptionist",
    board: AdLockScreen,
    hook: "A dusk lock screen: two missed calls, then Asha's notification — new patient booked, verified, forms sent.",
    inspiration: "Mailchimp's phone-native ad; notification-style creative from consumer apps.",
    why: "Missed new-patient calls are a quantifiable revenue leak every owner understands ($300+ LTV per call). Notification UI is camouflage in a phone feed — it reads as 'my phone,' not 'an ad,' until the payoff line lands.",
    copy: [
      {
        label: "Missed call",
        primaryText:
          "A new patient calls at 7:42 PM. Voicemail loses them to the next practice on Google. Asha's AI Receptionist answers every call — after hours, lunch rushes, Mondays — books the appointment, verifies insurance, and sends intake forms. One of 20 AI products in the first AI-native PMS.",
        headline: "Never lose another after-hours patient",
        cta: "Learn More",
      },
      {
        label: "Revenue leak",
        primaryText:
          "Every missed new-patient call is $1,000+ in lifetime value going to the next practice on Google. Asha's AI Receptionist answers every call — nights, lunch rushes, Mondays — books it, verifies it, sends the forms.",
        headline: "Answer every call. Even at 7:42 PM.",
        cta: "Learn More",
      },
      {
        label: "Voicemail",
        primaryText:
          "Your voicemail has never booked a patient. Asha's AI Receptionist has — it answers after hours, books the slot, verifies insurance, and sends intake forms before you're back in the office.",
        headline: "Voicemail doesn't book patients",
        cta: "Book Demo",
      },
    ],
    production: "Keep the notification card pixel-faithful to iOS for the camouflage effect. Story placement should be the priority for this one.",
  },
  {
    id: "night-shift",
    no: "08",
    title: "The Night Shift",
    tag: "all-in-one",
    board: AdNightShift,
    hook: "A timestamped overnight activity log — the platform breadth shown as a single night of work.",
    inspiration: "Evolution of Asha's own '20 products' checklist ads — same breadth, told as a story instead of a list.",
    why: "The checklist ads prove breadth but read as claims. The night-shift log proves the same breadth as activity — specific timestamps and counts feel like evidence. 'A PMS with a night shift' is a sticky, ownable phrase.",
    copy: [
      {
        label: "Activity log",
        primaryText:
          "11:38 PM — claims scrubbed. 1:12 AM — EOBs posted. 2:47 AM — every patient on today's schedule verified. 6:58 AM — this morning's cancellation filled. Asha is the first AI-native PMS: 20 dental AI products that work while your team sleeps.",
        headline: "The first PMS that works the night shift",
        cta: "Meet Asha",
      },
      {
        label: "Clock out",
        primaryText:
          "Your PMS clocks out when you do. Asha doesn't. Claims scrubbed at 11:38 PM. EOBs posted at 1:12 AM. Tomorrow's schedule verified by 3. The first AI-native PMS works the night shift, every night.",
        headline: "Your PMS clocks out. Asha doesn't.",
        cta: "Meet Asha",
      },
      {
        label: "8 AM payoff",
        primaryText:
          "Imagine walking in at 8 AM and the day is already handled: claims scrubbed, EOBs posted, every patient verified, the cancellation filled. With Asha, that's just Tuesday.",
        headline: "Walk in to a finished morning",
        cta: "Meet Asha",
      },
    ],
    production: "Natural video version: time-lapse sky with log entries appearing. The midnight→dawn gradient is the hero; keep entries to five max.",
  },
  {
    id: "group-chat",
    no: "09",
    title: "The Group Chat",
    tag: "all-in-one",
    board: AdGroupChat,
    hook: "Office managers' group chat — one of them is suspiciously done with her EOBs by 9:15 AM.",
    inspiration: "Asha's own iMessage skeptic ad (proven format), upgraded to a group-chat with named senders.",
    why: "The existing SMS ad performs because chat UI earns the read. The group chat adds social proof inside the format itself — peer envy ('how are you done??') is the most credible pitch an OM can hear.",
    copy: [
      {
        label: "Peer envy",
        primaryText:
          "When one office manager finishes EOBs by 9:15 AM, the group chat notices. Asha is a new type of PMS — 20 dental AI products in one platform that verify, post, scrub, recall, and answer phones automatically. See what your group chat is missing.",
        headline: "20 dental AI products in 1 platform",
        cta: "See For Yourself",
      },
      {
        label: "Relaxed one",
        primaryText:
          "\"How are you already done with EOBs??\" When your PMS does the busywork, you become the suspiciously relaxed one in the group chat. Asha: 20 dental AI products in one platform.",
        headline: "Be the relaxed one in the chat",
        cta: "See For Yourself",
      },
      {
        label: "Word travels",
        primaryText:
          "Office managers talk. When one of them stops doing verification calls, EOB posting, and recall lists entirely, the group chat finds out fast. Asha is the first AI-native PMS.",
        headline: "Your group chat is missing this",
        cta: "See For Yourself",
      },
    ],
    production: "Direct iteration on the proven PMSSMSAd — run head-to-head against the original to test 1:1 chat vs. group chat framing.",
  },
  {
    id: "the-wall",
    no: "10",
    title: "The Wall",
    tag: "social-proof",
    board: AdTestimonialWall,
    hook: "A stack of floating customer quote cards on Asha blue — switching stories, not feature claims.",
    inspiration: "Archy's comment-stack testimonial ad (their apparent workhorse format).",
    why: "PMS switching is high-fear; peer voice de-risks it better than any claim we can make. Specific, lived details ('if my staff went on vacation for a week, I'd rest easy') outperform superlatives.",
    copy: [
      {
        label: "Worth the switch",
        primaryText:
          "Dentists don't switch PMS for a marginal upgrade. They switch when the software starts doing the work — verification, billing, claims, phones, recall. 20 AI products in one platform. Hear it from the ones who made the jump.",
        headline: "The AI-native PMS worth switching for",
        cta: "Book Demo",
      },
      {
        label: "Loud switchers",
        primaryText:
          "Practices that switch to Asha don't whisper about it. The first AI-native PMS replaces the patchwork — verification, billing, claims, phones, recall — with 20 AI products in one platform. Hear it from the dentists who made the jump.",
        headline: "See why practices are switching",
        cta: "Book Demo",
      },
      {
        label: "De-risk",
        primaryText:
          "Switching PMS feels risky — until you talk to the practices that did it. Real dentists, real offices: faster collections, calmer front desks, easier days. Asha is the first AI-native PMS.",
        headline: "Talk to dentists who switched",
        cta: "Book Demo",
      },
    ],
    production: "Quotes are real customer sayings, attributed by role only (no names or practice names). Screenshot-real comment styling beats designed cards.",
  },
  {
    id: "position-filled",
    no: "11",
    title: "Position Filled",
    tag: "feature",
    feature: "AI Billing — claims, EOBs, denials, AR",
    board: AdJobPost,
    hook: "A job listing for dentistry's hardest hire — stamped POSITION FILLED, BY ASHA AI.",
    inspiration: "Job-board native formats; The Receipt's stamp payoff; salary-anchoring plays from B2B fintech ads.",
    why: "Every owner has lived this exact job post: billers are scarce, expensive to train, and quick to leave. The format camouflages as a job-board screenshot until the stamp lands the pitch in one beat — and it anchors Asha against a $60k salary plus turnover, not against other software budgets.",
    copy: [
      {
        label: "Job post",
        primaryText:
          "Hiring a dental biller takes months. Training takes longer. Then they leave. Asha's AI does the entire billing job — verifies benefits, submits clean claims, posts EOBs to the penny, fights denials, works your AR buckets, and sends patient statements. End to end, inside the first AI-native PMS. No job post required.",
        headline: "All your dental billing. Done by AI.",
        cta: "Book Demo",
      },
      {
        label: "Never hire",
        primaryText:
          "The hardest hire in dentistry is the one you never have to make. Asha's AI does the entire billing job — claims, EOBs, denials, AR, statements — end to end. No recruiter fees, no training, no turnover.",
        headline: "Stop hiring for a job AI does",
        cta: "Book Demo",
      },
      {
        label: "Salary anchor",
        primaryText:
          "A good biller costs $60k+, takes months to find, and leaves within two years. Asha's AI billing starts day one, never calls in sick, and works every claim to the penny — inside the first AI-native PMS.",
        headline: "All your billing. Done by AI.",
        cta: "Book Demo",
      },
      {
        label: "End to end",
        primaryText:
          "Most 'AI billing' tools do one slice — scrubbing, or posting, or follow-up. Asha does the whole job: verifies benefits, submits clean claims, posts EOBs, fights denials, works AR, sends statements. End to end.",
        headline: "Not a billing tool. The whole biller.",
        cta: "Book Demo",
      },
    ],
    production: "Static ships as-is. Video version: cursor scrolls a job board, pauses on the listing, stamp slams down with a thud. Check salary range against current market data per region before spend.",
  },
  {
    id: "the-denial",
    no: "12",
    title: "The Denial",
    tag: "feature",
    feature: "AI Billing — denials & appeals",
    board: AdDenialLetter,
    hook: "A payer denial letter — the most familiar defeat in dental billing — stamped APPEALED & PAID BY ASHA.",
    inspiration: "The Receipt and Position Filled stamp payoff, turned on the payer instead of the vendor stack.",
    why: "Denials are pure loss-aversion: owners know appeals go unworked because each one takes an hour. Showing the denial artifact itself stops the scroll, and the green stamp converts a familiar loss into a win with a specific dollar amount attached.",
    copy: [
      {
        label: "Appeals",
        primaryText:
          "Denials aren't final — they're a workflow. Asha's AI appeals automatically with the narrative, x-rays, and perio charting attached, then follows up until it's paid. Most practices never work denials. Asha works all of them.",
        headline: "Asha doesn't take no for an answer",
        cta: "Book Demo",
      },
      {
        label: "Found money",
        primaryText:
          "The average practice writes off most denied claims — not because they're unwinnable, but because each appeal takes an hour. Asha's AI files them in seconds and chases every one to payment.",
        headline: "Denied isn't done",
        cta: "Book Demo",
      },
      {
        label: "Zero effort",
        primaryText:
          "Claim denied at 2:14 PM. Appeal filed at 2:15 PM — narrative written, attachments included. Paid 11 days later. Nobody on your team touched it. That's AI billing inside the first AI-native PMS.",
        headline: "Appeals, filed while you drill",
        cta: "Book Demo",
      },
    ],
    production: "Static ships as-is. Video version: the letter slides in, a beat of silence on DENIED, then the stamp slams with the paid amount counting up. Use a plausible reason code; avoid real payer logos.",
  },
  {
    id: "to-the-penny",
    no: "13",
    title: "To the Penny",
    tag: "feature",
    feature: "AI Billing — EOB posting & reconciliation",
    board: AdLedger,
    hook: "An overnight posting ledger that balances to $0.00 variance — proof, not promise.",
    inspiration: "The Night Shift's timestamped-evidence format, applied to the single most error-prone task in billing.",
    why: "Office managers live in fear of deposits that don't match the PMS. 'Variance $0.00' is the most credible flex in dental billing — numbers read as evidence where adjectives read as ads.",
    copy: [
      {
        label: "Penny-perfect",
        primaryText:
          "Hand-keying EOBs invites typos, and typos become write-offs. Asha's AI posts every EOB overnight and reconciles to the penny — line items, adjustments, patient portions, all of it.",
        headline: "Reconciled to the penny, every night",
        cta: "Book Demo",
      },
      {
        label: "Trust math",
        primaryText:
          "If your deposits and your PMS disagree, somebody's weekend disappears. Asha posts EOBs with zero variance, every night, and flags anything a human should actually see.",
        headline: "Your deposits will finally match",
        cta: "Book Demo",
      },
      {
        label: "Morning zero",
        primaryText:
          "47 EOBs in the queue at close. Zero in the queue at open. Asha's AI posts, adjusts, and reconciles overnight — your team starts the day at inbox zero.",
        headline: "EOB inbox zero, every morning",
        cta: "Book Demo",
      },
    ],
    production: "Works as a 6s video: ledger rows type themselves terminal-style, the variance line lands last. Keep amounts plausible; payers and fee schedules vary by region.",
  },
  {
    id: "hero-eob",
    no: "14",
    title: "The Hero Feature",
    tag: "feature",
    feature: "Series template — AI EOB Posting shown",
    board: AdHeroFeature,
    hook: "One feature gets the hero graphic and the headline; everything else appears as a wall of chips underneath — 'oh wow, they have all this other stuff.'",
    inspiration: "Classic hook-then-upsell direct response structure; the AI OS deck's full product inventory as the supporting cast.",
    why: "Single-feature ads convert because the promise is concrete, but they undersell the platform. The chip wall fixes that in the same unit: the hook earns the stop, the inventory earns the click. This is a TEMPLATE — swap the hero graphic + headline for any of the 20 AI products and rerun, keeping the chip wall constant.",
    copy: [
      {
        label: "EOB hook",
        primaryText:
          "Every EOB, posted overnight, reconciled to the penny — automatically. And that's one of 20 AI products inside Asha: reception, scheduling, recall, intake, verification, claims, denials, collections, scribe, treatment planning, and a full PMS with imaging, phones, and texting. One platform. One login.",
        headline: "AI that posts your EOBs. Plus 20 more AIs.",
        cta: "Learn More",
      },
      {
        label: "Iceberg",
        primaryText:
          "You came for the AI that posts EOBs automatically. You'll stay for the other 30: AI reception, scheduling, recall, billing, denials, collections, scribe — plus a full PMS, SOTA cloud imaging, DoseSpot ePrescribe, phones, and texting. All one platform.",
        headline: "The EOB AI is just the start",
        cta: "See Everything",
      },
      {
        label: "Series voice",
        primaryText:
          "This week's spotlight: AI EOB Posting — every EOB posted overnight, zero variance. Same platform, 30 more where that came from. Asha is the world's only AI operating system for dentistry.",
        headline: "One platform. 20 AIs. 10+ softwares.",
        cta: "Learn More",
      },
    ],
    production:
      "SERIES FORMAT: duplicate this board and swap the hero card + eyebrow + headline per feature (AI Receptionist call card, AI Denial stamp, AI Recall list…). Chip wall and CTA stay identical for brand consistency. Run as a rotating sequence so the audience sees a different hero weekly.",
  },
];

const TAGS = [
  ["all", "All concepts"],
  ["category", "First AI-native"],
  ["all-in-one", "All-in-one"],
  ["feature", "Feature-led"],
  ["social-proof", "Social proof"],
];

const TAG_LABELS = {
  category: "First AI-native",
  "all-in-one": "All-in-one",
  feature: "Feature-led",
  "social-proof": "Social proof",
};

/* ----------------------------------------------------------------------------
   EXPORT — high-res PNG at Instagram resolutions.
   Boards are 340x604. Export stages render at 1/3 final size; toPng captures
   at pixelRatio 3 → exactly 1080px wide output.
   - square 1080x1080 & portrait 1080x1350: board scaled to fit, floating over
     a blurred copy of itself as the backdrop.
   - story 1080x1920: full-bleed (340:604 ≈ 9:16 within half a pixel).
   - story SAFE 1080x1920: card kept inside IG's safe area (~250px top /
     ~340px bottom are covered by profile/CTA overlays on Stories & Reels).
---------------------------------------------------------------------------- */

const BOARD_W = 340;
const BOARD_H = 604;

const EXPORT_FORMATS = {
  square: { w: 360, h: 360, label: "1:1", file: "1080x1080" },
  portrait: { w: 360, h: 450, label: "4:5", file: "1080x1350" },
  story: { w: 360, h: 640, label: "9:16", file: "1080x1920", fullBleed: true },
  storySafe: {
    w: 360,
    h: 640,
    label: "9:16 SAFE",
    file: "1080x1920-safe",
    safe: { top: 84, bottom: 114 },
  },
};

const exportLayout = (fmt) => {
  if (fmt.fullBleed) {
    const scale = Math.max(fmt.w / BOARD_W, fmt.h / BOARD_H);
    return {
      scale,
      round: false,
      x: (fmt.w - BOARD_W * scale) / 2,
      y: (fmt.h - BOARD_H * scale) / 2,
    };
  }
  const safeTop = fmt.safe ? fmt.safe.top : 0;
  const safeBottom = fmt.safe ? fmt.safe.bottom : 0;
  const availH = fmt.h - safeTop - safeBottom;
  const pad = fmt.safe ? 12 : 20;
  const scale = (availH - pad * 2) / BOARD_H;
  return {
    scale,
    round: true,
    x: (fmt.w - BOARD_W * scale) / 2,
    y: safeTop + (availH - BOARD_H * scale) / 2,
  };
};

const ExportRow = ({ ad, exporting, onExport }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(EXPORT_FORMATS).map(([key, f]) => {
      const busy =
        exporting && exporting.ad.id === ad.id && exporting.formatKey === key;
      return (
        <button
          key={key}
          disabled={!!exporting}
          title={`Export PNG ${f.file.replace("-safe", " (safe zones)")}`}
          onClick={(e) => {
            e.stopPropagation();
            onExport(ad, key);
          }}
          style={mono}
          className={`text-[9px] tracking-[0.1em] px-2 py-1 rounded border transition-colors ${
            busy
              ? "border-blue-400 bg-blue-500/20 text-blue-200"
              : "border-white/15 text-stone-400 hover:border-blue-300/50 hover:text-blue-200 disabled:opacity-40"
          }`}
        >
          {busy ? "…" : `↓ ${f.label}`}
        </button>
      );
    })}
  </div>
);

/* ----------------------------------------------------------------------------
   PAGE CHROME
---------------------------------------------------------------------------- */

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      style={mono}
      className="text-[10px] tracking-[0.1em] px-2.5 py-1 rounded border border-blue-300/30 text-blue-200/90 hover:bg-blue-400/10 transition-colors"
    >
      {copied ? "COPIED ✓" : label}
    </button>
  );
};

const NoteBlock = ({ label, children, copyText }) => (
  <div className="border-t border-white/10 pt-4">
    <div className="flex items-center justify-between mb-1.5">
      <div style={mono} className="text-[10px] tracking-[0.22em] text-blue-300/70">
        {label}
      </div>
      {copyText && <CopyButton text={copyText} label="COPY" />}
    </div>
    <div className="text-[13.5px] leading-relaxed text-stone-300">{children}</div>
  </div>
);

const AdLabPage = () => {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [variantIdx, setVariantIdx] = useState(0);
  const [exporting, setExporting] = useState(null); // { ad, formatKey }
  const [previewUrl, setPreviewUrl] = useState(null);
  const stageRef = useRef(null);
  const debugPreview = useMemo(
    () => new URLSearchParams(window.location.search).has("preview"),
    []
  );

  const activeCopy = selected
    ? selected.copy[variantIdx] || selected.copy[0]
    : null;

  const visible = useMemo(
    () => (filter === "all" ? ADS : ADS.filter((a) => a.tag === filter)),
    [filter]
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  const startExport = (ad, formatKey) => setExporting({ ad, formatKey });

  useEffect(() => {
    if (!exporting) return;
    let cancelled = false;
    (async () => {
      try {
        await document.fonts.ready;
        await new Promise((r) => setTimeout(r, 150)); // let the stage paint
        if (cancelled || !stageRef.current) return;
        const fmt = EXPORT_FORMATS[exporting.formatKey];
        const dataUrl = await toPng(stageRef.current, {
          pixelRatio: 3,
          width: fmt.w,
          height: fmt.h,
          cacheBust: true,
          // the live stage sits fixed at left:-99999; fixed-position nodes
          // don't rasterize inside foreignObject, so neutralize both in the clone
          style: { position: "static", left: "0px", top: "0px", margin: "0" },
        });
        if (cancelled) return;
        if (debugPreview) {
          setPreviewUrl(dataUrl);
        } else {
          const a = document.createElement("a");
          a.href = dataUrl;
          a.download = `asha-ad-${exporting.ad.id}-${fmt.file}.png`;
          a.click();
        }
      } catch (err) {
        console.error("Ad export failed:", err);
        alert(`Export failed: ${err.message}`);
      } finally {
        if (!cancelled) setExporting(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exporting, debugPreview]);

  // Offscreen export stage
  const renderStage = () => {
    if (!exporting) return null;
    const fmt = EXPORT_FORMATS[exporting.formatKey];
    const layout = exportLayout(fmt);
    const BoardComp = exporting.ad.board;
    const coverScale = Math.max(fmt.w / BOARD_W, fmt.h / BOARD_H);
    return (
      <div
        ref={stageRef}
        style={{
          position: "fixed",
          left: -99999,
          top: 0,
          width: fmt.w,
          height: fmt.h,
          overflow: "hidden",
          background: "#0d1b3d",
        }}
      >
        {!fmt.fullBleed && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: BOARD_W,
              height: BOARD_H,
              transform: `translate(-50%, -50%) scale(${coverScale * 1.3})`,
              filter: "blur(22px) brightness(0.7) saturate(1.15)",
            }}
          >
            <BoardComp />
          </div>
        )}
        <div
          className={layout.round ? "" : "adlab-noround"}
          style={{
            position: "absolute",
            left: layout.x,
            top: layout.y,
            width: BOARD_W,
            height: BOARD_H,
            transform: `scale(${layout.scale})`,
            transformOrigin: "top left",
            boxShadow: layout.round ? "0 24px 64px rgba(0,0,0,.5)" : "none",
            borderRadius: layout.round ? 24 : 0,
          }}
        >
          <BoardComp />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen text-stone-200" style={{ background: "#0b0e17" }}>
      <style>{FONTS}</style>
      <style>{`
        @keyframes adlab-rise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
        .adlab-card { animation: adlab-rise .6s cubic-bezier(.2,.7,.2,1) both; }
        .adlab-card:hover .adlab-board { transform: translateY(-6px); box-shadow: 0 30px 60px rgba(0,0,0,.55), 0 0 0 1px rgba(147,197,253,.25); }
        .adlab-board { transition: transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s; box-shadow: 0 18px 44px rgba(0,0,0,.45), 0 0 0 1px rgba(255,255,255,.07); }
        .adlab-noround > div { border-radius: 0 !important; }
      `}</style>

      {/* grain + glow atmosphere */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.06]"
        style={{ backgroundImage: GRAIN, backgroundSize: "220px" }}
      />
      <div
        className="pointer-events-none fixed inset-x-0 top-0 h-[420px]"
        style={{
          background:
            "radial-gradient(60% 100% at 50% 0%, rgba(59,130,246,.22), transparent 70%)",
        }}
      />

      {/* header */}
      <header className="relative max-w-[1240px] mx-auto px-6 pt-14 pb-4">
        <div className="flex items-center justify-between">
          <div style={mono} className="text-[11px] tracking-[0.28em] text-blue-300/80">
            ASHA DENTAL · INTERNAL CREATIVE
          </div>
          <div style={mono} className="text-[11px] tracking-[0.18em] text-stone-500">
            META · IG + FB · JUNE 2026
          </div>
        </div>
        <h1 style={serif} className="mt-6 text-[56px] md:text-[76px] leading-[0.98] font-medium text-white">
          The Ad Lab<span className="text-blue-400">.</span>
        </h1>
        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-stone-400">
          Thirteen concepts for the first AI-native dental PMS — rendered live,
          not mocked. Click any board for the strategy, the why, and
          ready-to-paste Meta copy in multiple selectable variations.
        </p>

        {/* filters */}
        <div className="mt-8 flex flex-wrap gap-2">
          {TAGS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={mono}
              className={`text-[11px] tracking-[0.12em] uppercase px-4 py-2 rounded-full border transition-colors ${
                filter === key
                  ? "bg-blue-500 border-blue-400 text-white"
                  : "border-white/15 text-stone-400 hover:border-blue-300/50 hover:text-blue-200"
              }`}
            >
              {label}
              <span className="ml-2 opacity-60">
                {key === "all" ? ADS.length : ADS.filter((a) => a.tag === key).length}
              </span>
            </button>
          ))}
        </div>
      </header>

      {/* board grid */}
      <main
        className="relative max-w-[1240px] mx-auto px-6 pt-10 pb-28 grid gap-x-10 gap-y-16 justify-center"
        style={{ gridTemplateColumns: "repeat(auto-fill, 340px)" }}
      >
        {visible.map((ad, i) => {
          const BoardComp = ad.board;
          return (
            <div
              key={ad.id}
              className="adlab-card text-left group"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div
                role="button"
                tabIndex={0}
                className="cursor-pointer"
                onClick={() => {
                  setSelected(ad);
                  setVariantIdx(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setSelected(ad);
                    setVariantIdx(0);
                  }
                }}
              >
                <div className="flex items-baseline justify-between mb-3 px-1">
                  <div className="flex items-baseline gap-3">
                    <span style={serif} className="text-[22px] italic text-blue-300/90">
                      {ad.no}
                    </span>
                    <span className="text-[15px] font-semibold text-white">
                      {ad.title}
                    </span>
                  </div>
                  <span
                    style={mono}
                    className="text-[9px] tracking-[0.16em] uppercase text-stone-500 group-hover:text-blue-300 transition-colors"
                  >
                    {TAG_LABELS[ad.tag]}
                  </span>
                </div>
                <div className="adlab-board rounded-[24px]">
                  <BoardComp />
                </div>
              </div>
              <div className="mt-3 px-1 flex items-center justify-between gap-2">
                <ExportRow ad={ad} exporting={exporting} onExport={startExport} />
                <button
                  onClick={() => {
                    setSelected(ad);
                    setVariantIdx(0);
                  }}
                  style={mono}
                  className="text-[10px] text-blue-300/50 group-hover:text-blue-300/90 hover:text-blue-200 transition-colors whitespace-nowrap"
                >
                  STRATEGY →
                </button>
              </div>
            </div>
          );
        })}
      </main>

      <footer className="relative max-w-[1240px] mx-auto px-6 pb-16">
        <div style={mono} className="text-[10px] tracking-[0.14em] text-stone-600 leading-relaxed">
          INTERNAL CONCEPTS — NOT CREATIVE-FINAL. COMPETITOR PRICING ON “THE
          RECEIPT” TO BE VERIFIED BEFORE SPEND.
        </div>
      </footer>

      {/* strategy drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-stretch justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-[880px] h-full overflow-y-auto"
            style={{ background: "#0e1220", boxShadow: "-30px 0 80px rgba(0,0,0,.6)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-8 py-5"
              style={{ background: "rgba(14,18,32,.92)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(255,255,255,.08)" }}
            >
              <div className="flex items-baseline gap-4">
                <span style={serif} className="text-[30px] italic text-blue-300">
                  {selected.no}
                </span>
                <div>
                  <div style={serif} className="text-[24px] text-white leading-none">
                    {selected.title}
                  </div>
                  <div style={mono} className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mt-1.5">
                    {TAG_LABELS[selected.tag]}
                    {selected.feature ? ` · ${selected.feature}` : ""}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                style={mono}
                className="text-[11px] tracking-[0.12em] px-3.5 py-2 rounded-full border border-white/15 text-stone-400 hover:text-white hover:border-white/40 transition-colors"
              >
                ESC ✕
              </button>
            </div>

            <div className="px-8 py-8 flex flex-col lg:flex-row gap-10">
              <div className="shrink-0 mx-auto lg:mx-0">
                <div className="adlab-board rounded-[24px]">
                  {React.createElement(selected.board)}
                </div>
                <div style={mono} className="mt-4 mb-1.5 text-[9px] tracking-[0.2em] text-blue-300/60">
                  EXPORT PNG · 1080PX · IG-READY
                </div>
                <ExportRow ad={selected} exporting={exporting} onExport={startExport} />
              </div>

              <div className="flex-1 space-y-5 min-w-0">
                <div>
                  <div style={mono} className="text-[10px] tracking-[0.22em] text-blue-300/70 mb-1.5">
                    THE HOOK
                  </div>
                  <div style={serif} className="text-[20px] leading-snug text-white">
                    {selected.hook}
                  </div>
                </div>

                <NoteBlock label="WHY IT WORKS">{selected.why}</NoteBlock>
                <NoteBlock label="INSPIRATION">{selected.inspiration}</NoteBlock>
                <div className="border-t border-white/10 pt-4">
                  <div
                    style={mono}
                    className="text-[10px] tracking-[0.22em] text-blue-300/70 mb-2.5"
                  >
                    COPY VARIATIONS
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected.copy.map((c, i) => (
                      <button
                        key={c.label}
                        onClick={() => setVariantIdx(i)}
                        style={mono}
                        className={`text-[10px] tracking-[0.08em] uppercase px-3 py-1.5 rounded-full border transition-colors ${
                          i === variantIdx
                            ? "bg-blue-500 border-blue-400 text-white"
                            : "border-white/15 text-stone-400 hover:border-blue-300/50 hover:text-blue-200"
                        }`}
                      >
                        {String.fromCharCode(65 + i)} · {c.label}
                      </button>
                    ))}
                  </div>
                </div>
                <NoteBlock label="PRIMARY TEXT (META)" copyText={activeCopy.primaryText}>
                  <span className="text-stone-200">{activeCopy.primaryText}</span>
                </NoteBlock>
                <NoteBlock
                  label="HEADLINE + CTA"
                  copyText={`${activeCopy.headline} — [${activeCopy.cta}]`}
                >
                  <span className="text-stone-200">{activeCopy.headline}</span>
                  <span
                    style={mono}
                    className="ml-3 inline-block text-[10px] tracking-[0.1em] px-2.5 py-1 rounded bg-blue-500/20 text-blue-200 border border-blue-400/30"
                  >
                    {activeCopy.cta.toUpperCase()}
                  </span>
                </NoteBlock>
                <NoteBlock label="PRODUCTION NOTES">{selected.production}</NoteBlock>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* offscreen export stage */}
      {renderStage()}

      {/* debug preview overlay (?preview=1) */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-8 cursor-pointer"
          onClick={() => setPreviewUrl(null)}
        >
          <img
            id="adlab-preview-img"
            src={previewUrl}
            alt="Export preview"
            className="max-h-full max-w-full rounded shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

export default AdLabPage;
